import React, { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import {
  extractGoogleDriveFileId,
  getGoogleDriveThumbnailUrl,
  getNotePreviewKind,
} from '../utils/noteFilePreview';

export interface NoteDocumentPreviewProps {
  fileUrl: string;
  fileName?: string;
  /** Kart içi kompakt yükseklik (px) */
  height?: number;
  className?: string;
  /** Admin paneli gibi — harici Drive bağlantısı açma butonları */
  interactive?: boolean;
}

const NoteDocumentPreview: React.FC<NoteDocumentPreviewProps> = ({
  fileUrl,
  fileName,
  height = 160,
  className,
  interactive = false,
}) => {
  const [thumbFailed, setThumbFailed] = useState(false);
  const kind = useMemo(() => getNotePreviewKind(fileUrl, fileName), [fileUrl, fileName]);
  const driveId = useMemo(() => extractGoogleDriveFileId(fileUrl), [fileUrl]);

  const shell: React.CSSProperties = {
    width: '100%',
    height,
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(51, 65, 85, 0.35)',
    background: 'rgba(15, 23, 42, 0.65)',
    position: 'relative',
    marginBottom: '14px',
  };

  if (!fileUrl?.trim()) {
    return (
      <div className={className} style={{ ...shell, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#64748b', fontSize: '12px' }}>Dosya bağlantısı yok</span>
      </div>
    );
  }

  if (kind === 'drive' && driveId && !thumbFailed) {
    return (
      <div className={className} style={shell}>
        <img
          src={getGoogleDriveThumbnailUrl(driveId, 640)}
          alt={fileName || 'Not kapak görseli'}
          loading="lazy"
          onError={() => setThumbFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
          draggable={false}
        />
        {fileName ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              background: 'linear-gradient(transparent 50%, rgba(0,0,0,0.7))',
              padding: '10px 12px',
              pointerEvents: 'none',
            }}
          >
            <span
              style={{
                color: '#e2e8f0',
                fontSize: '11px',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fileName}
            </span>
          </div>
        ) : null}
      </div>
    );
  }

  if (kind === 'drive' && driveId && thumbFailed) {
    return (
      <div
        className={className}
        style={{
          ...shell,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          padding: '16px',
          textAlign: 'center',
        }}
      >
        <FileText size={28} color="#64748b" />
        <span style={{ color: '#94a3b8', fontSize: '12px', lineHeight: 1.5, maxWidth: '280px' }}>
          {fileName || 'Dosya'} — indirmek için giriş yapın
        </span>
      </div>
    );
  }

  if (kind === 'pdf' && !interactive) {
    return (
      <div
        className={className}
        style={{
          ...shell,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '12px',
        }}
      >
        <FileText size={28} color="#64748b" />
        <span style={{ color: '#94a3b8', fontSize: '12px' }}>{fileName || 'PDF dosyası'}</span>
      </div>
    );
  }

  if (kind === 'pdf') {
    return (
      <div className={className} style={shell}>
        <iframe
          title={fileName || 'PDF önizleme'}
          src={`${fileUrl}#toolbar=0&navpanes=0`}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <div className={className} style={shell}>
        <img
          src={fileUrl}
          alt={fileName || 'Not görseli'}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: interactive ? 'auto' : 'none' }}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        ...shell,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
      }}
    >
      <FileText size={28} color="#64748b" />
      <span style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'center' }}>
        {fileName || 'Dosya'}
      </span>
    </div>
  );
};

export default NoteDocumentPreview;
